import React from 'react';
// import { Button } from '@/components/ui/button'; // This was unused
import { motion } from 'framer-motion';
import { Wallet, Puzzle, Container, Lock, ArrowDownUp, Code, ArrowRight } from 'lucide-react'; // Placeholder icon

const WalletStorageManagement: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          <div className="lg:w-1/2">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-500"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Wallet Storage & Management
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl font-medium mb-8 text-foreground/80"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Robust wallet storage solutions coupled with efficient management of digital assets.
            </motion.p>
            {/* Combined CTA Buttons Container - Using Button component styles */}
            <motion.div
              className="flex flex-col md:flex-row flex-wrap items-center gap-4 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Documentation Button - Styled like Shadcn Button */}
              <a
                href="https://docs.projectbabbage.com/docs/concepts/wallets"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Learn more about Project Babbage Wallets in the documentation"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-8 gap-2"
                role="button"
              >
                Learn More
                <ArrowRight className="w-4 h-4" />
              </a>
              {/* Contact CTA Button - Styled like Shadcn Button */}
              <a
                href="/Contact"
                aria-label="Contact us to get started"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8 gap-2"
                role="button"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
          <motion.div 
            className="lg:w-1/2 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Wallet className="h-48 w-48 text-primary/50" />
          </motion.div>
        </div>
      </section>

      {/* Key Features Section - Styled like OverlayHostingServices */}
      <section className="py-20 bg-muted/50">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4 text-primary"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Key Features
            </motion.h2>
            <motion.p 
               className="text-muted-foreground max-w-2xl mx-auto"
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               viewport={{ once: true }}
               transition={{ duration: 0.5, delay: 0.1 }}
             > 
               Exploring the core functionalities of Babbage Wallets and Storage.
             </motion.p>
           </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Puzzle className="h-8 w-8 text-primary" />,
                title: "Unified Wallet Interface",
                description: "Babbage aligns with an open standard so that wallet functionality—transaction creation, signing, certificate management, etc.—remains consistent across implementations. This consistency makes it easier for users to switch wallets without sacrificing access to their assets or losing essential features."
              },
              {
                icon: <Container className="h-8 w-8 text-primary" />,
                title: "UTXO Tracking with Baskets",
                description: "The wallet marks unspent outputs (UTXOs) as \"tokens.\" It organizes them into named containers called \"baskets,\" which let users (and applications) group or filter specific outputs. Baskets simplify token management by streamlining how apps track and spend certain sets of UTXOs."
              },
              {
                icon: <Lock className="h-8 w-8 text-primary" />,
                title: "Encrypted Storage & Data Ownership",
                description: "By default, data is encrypted client-side before being placed into storage. This design ensures only authorized parties can read the contents. Users keep control over their private keys and determine exactly who can access which data."
              },
              {
                icon: <ArrowDownUp className="h-8 w-8 text-primary" />,
                title: "Transaction Internalization",
                description: "When a new payment or insertion of data arrives, the wallet \"internalizes\" it by labeling, tagging, and optionally converting that transaction\'s outputs into basket-tracked tokens. This preserves a clear on-chain record while ensuring the wallet organizes each output for quick retrieval."
              },
              {
                icon: <Code className="h-8 w-8 text-primary" />,
                title: "Extensible Permission Schemes",
                description: "Project Babbage\'s approach allows advanced or future \"permission schemes,\" so that baskets, token types, or protocol IDs remain extensible. New features—like specialized token categories or advanced encryption—can roll out without breaking older apps or wallets."
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="bg-card border rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="mb-4 bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WalletStorageManagement; 